// Chakra imports
import { Portal, Box, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin.js';
import Navbar from 'components/navbar/NavbarAdmin.js';
import Sidebar from 'components/sidebar/Sidebar.js';
import { SidebarContext } from 'contexts/SidebarContext';

import React, {
  useState,
  useEffect,
  useRef,
  useCallback
} from 'react';

import { Redirect, Route, Switch, useLocation, useHistory } from 'react-router-dom';
import routes from 'routes.js';
import LanguageSwitcher from 'components/LanguageSwitcher';

// SOCKET + BADGE
import useOrderSocket from "hooks/useOrderSocket";
import { useUnseenOrders } from "contexts/UnseenOrdersContext";
import { useOrders } from "contexts/OrdersContext";

export default function Dashboard(props) {
  const history = useHistory();
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);

  const { addNewOrder } = useOrders();
  const { incrementUnseen, resetUnseen } = useUnseenOrders();

  const newOrderSoundRef = useRef(null);
  const { ...rest } = props;

  // States
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [accessToken, setAccessToken] = useState(sessionStorage.getItem('accessToken'));

  const { onOpen } = useDisclosure();

  // -----------------------------
  // 🔊 LOAD SOUND
  // -----------------------------
  useEffect(() => {
    newOrderSoundRef.current = new Audio("/sounds/new_order.wav");
    newOrderSoundRef.current.volume = 0.7;
  }, []);

  // -----------------------------
  // 🔄 TRACK CURRENT PAGE
  // -----------------------------
  useEffect(() => {
    pathnameRef.current = location.pathname;

    if (location.pathname.startsWith("/admin/orders")) {
      resetUnseen();
    }
  }, [location.pathname, resetUnseen]);

  // -----------------------------
  // 🔥 SOCKET CALLBACK
  // -----------------------------
  const handleNewOrder = useCallback(
    (order) => {
      if (!order || !order.order_id) return;

      addNewOrder(order);

      // SOUND
      if (newOrderSoundRef.current) {
        try {
          newOrderSoundRef.current.currentTime = 0;
          newOrderSoundRef.current.play();
        } catch (_) {}
      }

      // BADGE
      if (!pathnameRef.current.startsWith("/admin/orders")) {
        incrementUnseen();
      }
    },
    [addNewOrder, incrementUnseen]
  );

  // -----------------------------
  // 🔌 SOCKET SUBSCRIBE
  // -----------------------------
  useOrderSocket(handleNewOrder);

  // -----------------------------
  // NAV HELPERS
  // -----------------------------
  const getRoute = () => location.pathname !== '/admin/full-screen-maps';

  const getActiveRoute = (routes) => {
    let activeRoute = 'Main Dashboard';
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let route = getActiveRoute(routes[i].items);
        if (route !== activeRoute) return route;
      } else if (routes[i].category) {
        let route = getActiveRoute(routes[i].items);
        if (route !== activeRoute) return route;
      } else if (location.pathname.includes(routes[i].layout + routes[i].path)) {
        return routes[i].name;
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let res = getActiveNavbar(routes[i].items);
        if (res !== activeNavbar) return res;
      } else if (routes[i].category) {
        let res = getActiveNavbar(routes[i].items);
        if (res !== activeNavbar) return res;
      } else if (location.pathname.includes(routes[i].layout + routes[i].path)) {
        return routes[i].secondary;
      }
    }
    return activeNavbar;
  };

  const getActiveNavbarText = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let res = getActiveNavbarText(routes[i].items);
        if (res !== activeNavbar) return res;
      } else if (routes[i].category) {
        let res = getActiveNavbarText(routes[i].items);
        if (res !== activeNavbar) return res;
      } else if (location.pathname.includes(routes[i].layout + routes[i].path)) {
        return routes[i].messageNavbar;
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routes) =>
    routes.map((prop, key) => {
      if (prop.layout === '/admin')
        return <Route path={prop.layout + prop.path} component={prop.component} key={key} />;
      if (prop.collapse) return getRoutes(prop.items);
      if (prop.category) return getRoutes(prop.items);
      return null;
    });

  // -----------------------------
  // TOKEN EXPIRATION
  // -----------------------------
  const isAccessTokenExpired = () => {
    const expiryDate = sessionStorage.getItem('accessTokenExpiry');
    if (!expiryDate) return true;
    return new Date().getTime() > parseInt(expiryDate);
  };

  const handleAutoLogout = () => {
    if (isAccessTokenExpired()) {
      sessionStorage.clear();
      setAccessToken(null);
      history.push('/auth/sign-in/default');
    }
  };

  useEffect(() => {
    const interval = setInterval(handleAutoLogout, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setAccessToken(sessionStorage.getItem('accessToken'));
  }, [location]);

  useEffect(() => {
    document.documentElement.dir = 'ltr';
  }, []);

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <Box>
      <SidebarContext.Provider value={{ toggleSidebar, setToggleSidebar }}>
        <Sidebar routes={routes} display="none" {...rest} />

        <Box
          float="right"
          minHeight="100vh"
          height="100%"
          overflow="auto"
          maxHeight="100%"
          w={{ base: '100%', xl: 'calc(100% - 290px)' }}
          maxWidth={{ base: '100%', xl: 'calc(100% - 290px)' }}
          transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
        >
          <Portal>
            <Box>
              <Navbar
                onOpen={onOpen}
                brandText={getActiveRoute(routes)}
                secondary={getActiveNavbar(routes)}
                message={getActiveNavbarText(routes)}
                fixed={fixed}
                {...rest}
              />
              {/* <LanguageSwitcher /> */}
            </Box>
          </Portal>

          {getRoute() && (
            <Box mx="auto" p={{ base: '20px', md: '30px' }} pe="20px" minH="100vh" pt="50px">
              <Switch>
                {getRoutes(routes)}
                <Redirect to={accessToken ? '/admin/default' : '/auth/sign-in/default'} />
              </Switch>
            </Box>
          )}

          <Box position="relative" mt="auto">
            <Footer />
          </Box>
        </Box>
      </SidebarContext.Provider>
    </Box>
  );
}
